import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Star, Gift, CreditCard, Users, CheckCircle2, ChevronRight, Sparkles, Calculator, Smartphone, QrCode, Gamepad2, MapPin, Crown, Share2, ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const LoyaltyPage = () => {
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);

  const questions = [
    { 
      id: "l1", 
      question: "Comment fidélisez-vous vos clients aujourd'hui ?",
      options: [
        "Programme de fidélité structuré (points, carte, app)",
        "Réductions ponctuelles et offres spéciales",
        "Newsletters et communication régulière",
        "Rien de particulier"
      ]
    },
    { 
      id: "l2", 
      question: "À quelle fréquence vos clients reviennent-ils acheter chez vous ?",
      options: [
        "Très souvent (1 fois/mois ou plus)",
        "Régulièrement (tous les 2-3 mois)",
        "Occasionnellement (2-3 fois par an)",
        "Rarement (1 fois par an ou moins)"
      ]
    },
    { 
      id: "l3", 
      question: "Quelle est votre relation avec vos clients après l'achat ?",
      options: [
        "Suivi personnalisé (emails, appels, SAV proactif)",
        "Emails automatiques (confirmation, relance)",
        "Communication sur les réseaux sociaux",
        "Aucun contact après l'achat"
      ]
    },
    { 
      id: "l4", 
      question: "Quel est votre plus grand défi en matière de fidélisation ?",
      options: [
        "Mes clients ne reviennent pas assez souvent",
        "Je n'arrive pas à me différencier de la concurrence",
        "Je ne connais pas assez mes clients",
        "Je ne sais pas par où commencer"
      ]
    },
    { 
      id: "l5", 
      question: "Disposez-vous d'une base de données clients exploitable ?",
      options: [
        "Oui, CRM complet avec historique d'achats",
        "Oui, fichier emails / téléphones basique",
        "Quelques contacts récoltés ici et là",
        "Non, pas du tout"
      ]
    }
  ];

  const programs = [
    {
      icon: Star,
      title: "Programme à Points",
      description: "Chaque achat rapporte des points convertibles en récompenses. Simple, efficace et apprécié.",
      features: ["Cumul automatique", "Paliers de récompenses", "Carte digitale"]
    },
    {
      icon: Gift,
      title: "Parrainage",
      description: "Transformez vos clients en ambassadeurs avec des avantages pour le parrain et le filleul.",
      features: ["Bonus parrain/filleul", "Suivi des parrainages", "Viralité naturelle"]
    },
    {
      icon: CreditCard,
      title: "Carte VIP",
      description: "Offrez un statut privilégié à vos meilleurs clients avec des avantages exclusifs.",
      features: ["Accès prioritaire", "Offres exclusives", "Événements privés"]
    }
  ];

  const handleAnswer = (answer) => {
    const newAnswers = [...answers, { question_id: questions[currentQuestion].id, answer }];
    setAnswers(newAnswers);

    setCurrentQuestion(currentQuestion + 1);
  };

  const submitQuiz = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quiz/submit`, {
        quiz_type: "loyalty",
        answers,
        email: email || null,
        company: company || null
      });
      setQuizResult(res.data);
      toast.success("Quiz complété !");
    } catch (e) {
      console.error("Quiz error:", e);
      toast.error("Erreur lors de la soumission du quiz");
    }
    setLoading(false);
  };

  const resetQuiz = () => {
    setShowQuiz(false);
    setCurrentQuestion(0);
    setAnswers([]);
    setQuizResult(null);
    setEmail("");
    setCompany("");
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="loyalty-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#8C9D78]/10 via-transparent to-[#C65D47]/10"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8C9D78]/10 text-[#8C9D78]">
                <Heart className="w-4 h-4" />
                <span className="text-sm font-medium">Fidéliser ses Clients</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1D3557] leading-tight" style={{ fontFamily: 'Fraunces, serif' }}>
                Transformez vos clients en{" "}
                <span className="text-[#8C9D78]">ambassadeurs fidèles</span>
              </h1>
              <p className="text-lg md:text-xl text-stone-600 max-w-xl">
                Programmes de fidélité innovants, parrainage, carte VIP... Créez une relation durable 
                avec vos clients et augmentez leur valeur vie.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => setShowQuiz(true)} 
                  className="bg-[#8C9D78] hover:bg-[#7A8B69] text-white rounded-full px-8 h-14 text-lg font-semibold shadow-lg"
                  data-testid="start-quiz-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Diagnostiquez votre fidélisation
                </Button>
                <Button asChild variant="outline" className="btn-secondary h-14 text-lg">
                  <Link to="/contact">Demander un devis</Link>
                </Button>
              </div>
            </div>

            <div className="relative hidden lg:block">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/7287a91fa440e464f68cb10ad030b556bd11260c6027814acd72e1f43d68c800.png"
                alt="Programme de fidélité"
                className="rounded-3xl shadow-2xl object-cover"
                style={{ maxHeight: 500 }}
              />
              <div className="absolute -bottom-6 -right-6 glass rounded-2xl p-6 animate-float">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#8C9D78] flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#1D3557] text-xl">+60%</p>
                    <p className="text-sm text-stone-500">Rétention client</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quiz Modal */}
      {showQuiz && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="quiz-modal">
          <Card className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl">
            <CardContent className="p-8">
              {!quizResult ? (
                <>
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-[#8C9D78]">
                        Question {currentQuestion + 1}/{questions.length}
                      </span>
                      <button onClick={resetQuiz} className="text-stone-400 hover:text-stone-600">
                        ✕
                      </button>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>

                  {currentQuestion < questions.length ? (
                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
                        {questions[currentQuestion].question}
                      </h3>
                      <div className="grid gap-4">
                        {questions[currentQuestion].options.map((option, i) => (
                          <button
                            key={i}
                            onClick={() => handleAnswer(option)}
                            className="quiz-option text-left hover:border-[#8C9D78]/50 hover:bg-[#8C9D78]/5"
                            data-testid={`quiz-option-${i}`}
                          >
                            <span className="font-medium text-[#1D3557]">{option}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
                        Dernière étape !
                      </h3>
                      <p className="text-stone-600">
                        Laissez-nous vos coordonnées pour recevoir votre analyse personnalisée.
                      </p>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="company">Entreprise</Label>
                          <Input
                            id="company"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            placeholder="Nom de votre entreprise"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="votre@email.com"
                          />
                        </div>
                      </div>
                      <Button
                        onClick={submitQuiz}
                        disabled={loading}
                        className="w-full bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full h-14"
                        data-testid="quiz-submit-btn"
                      >
                        {loading ? "Analyse en cours..." : "Voir mon résultat"}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center space-y-6" data-testid="quiz-result">
                  <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center bg-gradient-to-br from-[#8C9D78] to-[#6B7C5A]">
                    <Zap className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#8C9D78] uppercase tracking-wider mb-1">Votre profil</p>
                    <h3 className="text-2xl font-bold text-[#1D3557]" style={{ fontFamily: "Fraunces, serif" }}>
                      {quizResult.profile || "Résultat"}
                    </h3>
                    <p className="text-stone-500 mt-1">Score fidélisation : {quizResult.score}/100</p>
                  </div>

                  <div className="text-left bg-[#1D3557]/5 rounded-xl p-5">
                    {quizResult.recommendations.map((rec, i) => (
                      <p key={i} className="text-stone-600 text-sm leading-relaxed mb-2 last:mb-0 flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#8C9D78] flex-shrink-0 mt-0.5" />
                        {rec}
                      </p>
                    ))}
                  </div>

                  {quizResult.solutions && quizResult.solutions.length > 0 && (
                    <div className="text-left space-y-3">
                      <h4 className="font-bold text-[#1D3557] text-lg">Les solutions idéales pour vous :</h4>
                      {quizResult.solutions.map((sol, i) => (
                        <div key={i} className="bg-white border border-stone-200 rounded-xl p-4 hover:border-[#8C9D78]/50 hover:shadow-md transition-all">
                          <div className="flex items-start justify-between mb-1">
                            <h5 className="font-semibold text-[#1D3557]">{sol.name}</h5>
                            <span className="text-xs font-bold text-[#8C9D78] bg-[#8C9D78]/10 px-2.5 py-1 rounded-full whitespace-nowrap ml-2">{sol.impact}</span>
                          </div>
                          <p className="text-sm text-stone-500">{sol.desc}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-4 pt-2">
                    <Button onClick={resetQuiz} variant="outline" className="flex-1 rounded-full">Voir les solutions</Button>
                    <Button asChild className="flex-1 bg-[#C65D47] hover:bg-[#A84A38] rounded-full">
                      <Link to="/contact">Parlons de votre projet</Link>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* iPhone Mockups Gallery */}
      <section className="py-20 bg-[#F4F1EA]" data-testid="loyalty-mockups">
        <div className="container-custom">
          <div className="text-center mb-14">
            <span className="text-sm font-semibold text-[#C65D47] uppercase tracking-widest">Exemples concrets</span>
            <h2 className="text-4xl font-bold text-[#1D3557] mt-4 mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              Des solutions éprouvées,<br />accessibles aux TPE / PME
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Découvrez les formats de programmes de fidélité que nous pouvons déployer pour votre marque.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12 max-w-4xl mx-auto">
            {[
              { src: "https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/cedd0799a6d2c92a448c4b86f4e1344f2d4abfa1ffafc570c47baed89f667708.png", label: "Programme à Points", stat: "Style Starbucks Rewards" },
              { src: "https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/cc255874454fd57e379f7949cdf4cf977614004929f0a5ad76852698c9d3f133.png", label: "Parrainage", stat: "Style Uber / Airbnb" },
              { src: "https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/ed2fb9de38e6175140061ea23f437b72deba1740c3e3760f790e16499cec776d.png", label: "Carte VIP", stat: "Style Sephora / Amex" },
            ].map((mockup, i) => (
              <div key={i} className="group text-center">
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg group-hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2">
                  <img src={mockup.src} alt={mockup.label} className="w-full h-auto object-cover" loading="lazy" />
                </div>
                <div className="mt-4">
                  <p className="font-semibold text-[#1D3557] text-lg">{mockup.label}</p>
                  <p className="text-sm text-[#8C9D78] font-medium mt-1">{mockup.stat}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-semibold text-[#8C9D78] uppercase tracking-widest">Nos Solutions</span>
            <h2 className="text-4xl font-bold text-[#1D3557] mt-4" style={{ fontFamily: 'Fraunces, serif' }}>
              Programmes sur-mesure
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <div key={index} className="card-service">
                <div className="w-14 h-14 rounded-2xl bg-[#8C9D78]/10 flex items-center justify-center mb-6">
                  <program.icon className="w-7 h-7 text-[#8C9D78]" />
                </div>
                <h3 className="text-xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: 'Fraunces, serif' }}>
                  {program.title}
                </h3>
                <p className="text-stone-600 mb-4">{program.description}</p>
                <ul className="space-y-2">
                  {program.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle2 className="w-4 h-4 text-[#8C9D78]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Solutions Section */}
      <section className="py-24 bg-[#1D3557]" data-testid="tech-solutions">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-[#8C9D78] font-semibold uppercase tracking-widest text-sm">Innovation & Technologie</span>
            <h2 className="text-4xl font-bold text-white mt-4 mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              Fidélisez grâce aux nouvelles technologies
            </h2>
            <p className="text-white/60 max-w-3xl mx-auto text-lg">
              Allez plus loin qu'un simple programme de points. Exploitez le mobile, la géolocalisation, 
              la gamification et les réseaux sociaux pour créer une expérience de fidélité unique et mémorable.
            </p>
          </div>

          {/* Solution 1 - Wallet Pass */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
            <div className="order-2 lg:order-1 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8C9D78]/20 text-[#8C9D78]">
                <Smartphone className="w-4 h-4" />
                <span className="text-sm font-medium">Pass Wallet Mobile</span>
              </div>
              <h3 className="text-3xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                La carte de fidélité dans la poche de vos clients
              </h3>
              <p className="text-white/70 text-lg leading-relaxed">
                Fini les cartes oubliées ou perdues. Grâce à l'intégration Apple Wallet et Google Wallet, 
                votre carte de fidélité vit directement dans le smartphone de vos clients. 
                Elle se met à jour automatiquement à chaque achat et envoie des rappels intelligents.
              </p>
              <ul className="space-y-3">
                {["Intégration Apple Wallet & Google Wallet", "Mise à jour automatique du solde de points", "Notifications push ciblées depuis le wallet", "Zéro friction : pas d'app à télécharger"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-[#8C9D78] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative max-w-[280px] group">
                <div className="absolute -inset-4 bg-[#8C9D78]/20 rounded-3xl blur-2xl group-hover:bg-[#8C9D78]/30 transition-all duration-500" />
                <img src="https://static.prod-images.emergentagent.com/jobs/dd9c2003-907f-4092-a4f4-7b5dd0ce1af9/images/c6a116b1d9f354742f1a525772d5c663598135d1abffed8cff001679933b2331.png" alt="Pass Wallet Mobile" className="relative rounded-2xl shadow-2xl group-hover:-translate-y-2 transition-transform duration-500" loading="lazy" />
              </div>
            </div>
          </div>

          {/* Solution 2 - QR Code */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
            <div className="flex justify-center">
              <div className="relative max-w-[280px] group">
                <div className="absolute -inset-4 bg-[#C65D47]/20 rounded-3xl blur-2xl group-hover:bg-[#C65D47]/30 transition-all duration-500" />
                <img src="https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/4f209cfd2c61441836dffd7c939fef3cc27738df2f4e5cba58f0800390acf644.png" alt="QR Code en magasin" className="relative rounded-2xl shadow-2xl group-hover:-translate-y-2 transition-transform duration-500" loading="lazy" />
              </div>
            </div>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C65D47]/20 text-[#C65D47]">
                <QrCode className="w-4 h-4" />
                <span className="text-sm font-medium">QR Code en Magasin</span>
              </div>
              <h3 className="text-3xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                Scanner, cumuler, récompenser en un instant
              </h3>
              <p className="text-white/70 text-lg leading-relaxed">
                À chaque passage en caisse, vos clients scannent un QR code et cumulent instantanément leurs points. 
                Pas de saisie manuelle, pas d'erreur. L'expérience est fluide, rapide et ludique avec 
                une animation de points gagnés qui fidélise dès le premier achat.
              </p>
              <ul className="space-y-3">
                {["Scan en moins de 2 secondes", "Cumul de points instantané avec animation", "Historique des transactions en temps réel", "Compatible avec n'importe quelle caisse"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-[#C65D47] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Solution 3 - Gamification */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
            <div className="order-2 lg:order-1 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFD700]/20 text-[#FFD700]">
                <Gamepad2 className="w-4 h-4" />
                <span className="text-sm font-medium">Gamification</span>
              </div>
              <h3 className="text-3xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                Transformez la fidélité en jeu addictif
              </h3>
              <p className="text-white/70 text-lg leading-relaxed">
                Inspiré des mécaniques de Duolingo et Nike Run Club, votre programme de fidélité devient un jeu. 
                Vos clients progressent à travers des niveaux (Bronze, Argent, Or, Platine), débloquent des badges 
                et relèvent des défis pour gagner des récompenses exclusives. L'engagement monte en flèche.
              </p>
              <ul className="space-y-3">
                {["Système de niveaux et progression visuelle", "Badges et achievements à collectionner", "Défis hebdomadaires personnalisés", "Classements et esprit de compétition amicale"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-[#FFD700] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative max-w-[280px] group">
                <div className="absolute -inset-4 bg-[#FFD700]/15 rounded-3xl blur-2xl group-hover:bg-[#FFD700]/25 transition-all duration-500" />
                <img src="https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/2b71cd699ecc7c49ac3b59342d153301ff700ced5747638dec20980888708491.png" alt="Gamification fidélité" className="relative rounded-2xl shadow-2xl group-hover:-translate-y-2 transition-transform duration-500" loading="lazy" />
              </div>
            </div>
          </div>

          {/* Solution 4 - Geolocation */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
            <div className="flex justify-center">
              <div className="relative max-w-[280px] group">
                <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-2xl group-hover:bg-white/15 transition-all duration-500" />
                <img src="https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/3c2a2997e1a93e0ed227c22105676c0de81313f727e59895a21032e104f0b39b.png" alt="Notifications géolocalisées" className="relative rounded-2xl shadow-2xl group-hover:-translate-y-2 transition-transform duration-500" loading="lazy" />
              </div>
            </div>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white">
                <MapPin className="w-4 h-4" />
                <span className="text-sm font-medium">Notifications Géolocalisées</span>
              </div>
              <h3 className="text-3xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                Le bon message, au bon endroit, au bon moment
              </h3>
              <p className="text-white/70 text-lg leading-relaxed">
                Grâce à la géolocalisation, envoyez une offre personnalisée à vos clients quand ils passent 
                à proximité de votre point de vente. "Vous êtes à 50m ! Profitez de -15% sur votre produit favori." 
                Un outil redoutable pour générer du trafic en magasin et augmenter le taux de conversion.
              </p>
              <ul className="space-y-3">
                {["Détection de proximité automatique", "Offres personnalisées selon l'historique d'achat", "Statistiques de passages et conversions", "Respect du RGPD avec opt-in client"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-white/60 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Solutions 5 & 6 - Side by side */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Subscription */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-[#8C9D78]/30 transition-all group">
              <div className="flex justify-center mb-6">
                <div className="relative max-w-[200px]">
                  <img src="https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/f1fc8216f1315e056be664e09666ca3efb20bbf54dda480d4a0d8507c0f8f77b.png" alt="Programme d'abonnement" className="rounded-xl shadow-xl group-hover:-translate-y-1 transition-transform duration-500" loading="lazy" />
                </div>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#8C9D78]/20 text-[#8C9D78] mb-4">
                <Crown className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Programme d'Abonnement</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Fraunces, serif' }}>
                Le club premium qui fidélise par l'exclusivité
              </h3>
              <p className="text-white/60 leading-relaxed mb-4">
                Inspiré d'Amazon Prime, proposez un abonnement mensuel donnant accès à des avantages exclusifs : 
                livraison gratuite, prix membres, accès anticipé aux nouveautés et cadeaux d'anniversaire. 
                Vos clients s'engagent durablement et leur panier moyen augmente naturellement.
              </p>
              <ul className="space-y-2">
                {["Livraison gratuite illimitée", "Réductions membres permanentes", "Accès anticipé & ventes privées"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#8C9D78] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Social Rewards */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-[#C65D47]/30 transition-all group">
              <div className="flex justify-center mb-6">
                <div className="relative max-w-[200px]">
                  <img src="https://static.prod-images.emergentagent.com/jobs/a0d3b171-4eaf-4e7d-aa74-39f7b7085e9d/images/8f79f985e1556f5a49246ad0d84e338617558df808a277fe008ca8f64fb1d64e.png" alt="Récompenses sociales" className="rounded-xl shadow-xl group-hover:-translate-y-1 transition-transform duration-500" loading="lazy" />
                </div>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C65D47]/20 text-[#C65D47] mb-4">
                <Share2 className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Récompenses Sociales</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Fraunces, serif' }}>
                Quand vos clients deviennent vos meilleurs ambassadeurs
              </h3>
              <p className="text-white/60 leading-relaxed mb-4">
                Récompensez chaque interaction sociale : un avis Google, un partage Instagram, un parrainage WhatsApp. 
                Vos clients gagnent des points en parlant de vous, et vous bénéficiez d'une visibilité organique 
                qui vaut bien plus que n'importe quelle campagne publicitaire.
              </p>
              <ul className="space-y-2">
                {["Points pour avis Google & TripAdvisor", "Bonus partage Instagram & Facebook", "Parrainage WhatsApp intégré"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#C65D47] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA within section */}
          <div className="text-center mt-16">
            <Button asChild className="bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full px-10 h-14 text-lg font-semibold shadow-lg">
              <Link to="/contact">
                Discuter de mon projet
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Avant / Après - Comparateur dynamique */}
      <section className="section-padding bg-[#FDFBF7]" data-testid="comparateur-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#C65D47] uppercase tracking-widest">Impact concret</span>
            <h2 className="text-4xl font-bold text-[#1D3557] mt-4 mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              Avant / Après un programme de fidélité
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Visualisez la transformation de votre relation client en un coup d'oeil.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* AVANT */}
            <div className="group relative">
              <div className="bg-white rounded-3xl p-8 border-2 border-stone-200 h-full transition-all duration-500 group-hover:border-[#C65D47]/30 group-hover:shadow-lg">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-full bg-[#C65D47]/10 flex items-center justify-center">
                    <span className="text-[#C65D47] font-bold text-xl">-</span>
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest text-[#C65D47] font-semibold">Avant</span>
                    <h3 className="text-xl font-bold text-[#1D3557]">Sans programme</h3>
                  </div>
                </div>
                <div className="space-y-5">
                  {[
                    { label: "Taux de rétention", value: "15-25%", bar: 20 },
                    { label: "Fréquence d'achat", value: "1-2x / an", bar: 15 },
                    { label: "Panier moyen", value: "Stagnant", bar: 30 },
                    { label: "Connaissance client", value: "Floue", bar: 10 },
                    { label: "Bouche-à-oreille", value: "Aléatoire", bar: 20 },
                  ].map((item, i) => (
                    <div key={i}>
                      <div className="flex justify-between mb-1.5">
                        <span className="text-sm text-stone-500">{item.label}</span>
                        <span className="text-sm font-semibold text-stone-400">{item.value}</span>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-stone-300 rounded-full transition-all duration-1000" style={{ width: `${item.bar}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* APRÈS */}
            <div className="group relative">
              <div className="bg-gradient-to-br from-[#1D3557] to-[#2A4A6D] rounded-3xl p-8 h-full transition-all duration-500 group-hover:shadow-2xl group-hover:scale-[1.02]">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-full bg-[#8C9D78]/20 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-[#8C9D78]" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest text-[#8C9D78] font-semibold">Après</span>
                    <h3 className="text-xl font-bold text-white flex items-center gap-2 flex-wrap">Avec programme <img src="https://customer-assets.emergentagent.com/job_moreati-preview/artifacts/1lwcz7xi_Copie%20de%20nouveau%20logo%20moreati%20%281%29%20copie.png" alt="MOREATI" className="h-7 inline-block" /></h3>
                  </div>
                </div>
                <div className="space-y-5">
                  {[
                    { label: "Taux de rétention", value: "60-80%", bar: 75, color: "#8C9D78" },
                    { label: "Fréquence d'achat", value: "4-6x / an", bar: 65, color: "#8C9D78" },
                    { label: "Panier moyen", value: "+25 à 40%", bar: 70, color: "#C65D47" },
                    { label: "Connaissance client", value: "CRM 360°", bar: 90, color: "#FFD700" },
                    { label: "Bouche-à-oreille", value: "Parrainage actif", bar: 80, color: "#8C9D78" },
                  ].map((item, i) => (
                    <div key={i}>
                      <div className="flex justify-between mb-1.5">
                        <span className="text-sm text-white/60">{item.label}</span>
                        <span className="text-sm font-bold" style={{ color: item.color }}>{item.value}</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${item.bar}%`, backgroundColor: item.color }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 pt-6 border-t border-white/10 text-center">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Impact moyen constaté</p>
                  <p className="text-3xl font-bold text-[#FFD700]" style={{ fontFamily: "Fraunces, serif" }}>+40% de CA récurrent</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button asChild className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full px-10 h-14 text-lg font-semibold shadow-lg">
              <Link to="/contact">
                Transformer ma fidélisation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#8C9D78]">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
            Prêt à fidéliser vos clients ?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Créons ensemble le programme de fidélité adapté à votre entreprise
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setShowQuiz(true)}
              className="bg-white text-[#8C9D78] hover:bg-stone-100 rounded-full px-10 h-14 text-lg font-semibold"
            >
              Diagnostiquez votre fidélisation
            </Button>
            <Button asChild variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#8C9D78] rounded-full px-10 h-14 text-lg">
              <Link to="/contact">Demander un devis</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoyaltyPage;
